import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import {
  ApplicantAuthErrorPayload,
  isApplicantAuthError,
  requestApplicantOtp,
  verifyApplicantOtp,
} from "@/api/studentApi";

interface ApplicantToken {
  tenant_id?: number | null;
  role?: string;
}

const OTP_RESEND_COOLDOWN_SECONDS = 30;

function secondsUntil(target: number | null, now: number): number {
  if (!target) {
    return 0;
  }
  return Math.max(0, Math.ceil((target - now) / 1000));
}

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function authMessage(payload: ApplicantAuthErrorPayload): string {
  const retryAfter = payload.retry_after_seconds ?? 0;
  const retrySuffix =
    retryAfter > 0 ? ` Please retry after ${formatCountdown(retryAfter)}.` : "";

  switch (payload.error_code) {
    case "OTP_INVALID":
      return payload.detail || "Incorrect OTP. Please try again.";
    case "OTP_EXPIRED":
      return "OTP expired. Request a new code.";
    case "OTP_REPLAYED":
      return "This OTP was already used. Request a new code.";
    case "OTP_RATE_LIMIT":
      return `You requested OTP too many times.${retrySuffix}`;
    case "OTP_LOCKED":
    case "OTP_ATTEMPTS_EXCEEDED":
      return `Too many incorrect OTP attempts.${retrySuffix}`;
    case "OTP_RATE_LIMIT_UNAVAILABLE":
    case "OTP_SESSION_UNAVAILABLE":
    case "NETWORK_ERROR":
      return payload.detail;
    default:
      return payload.detail || "Authentication failed. Please retry.";
  }
}

const ApplicantLogin: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const navigate = useNavigate();
  const slug = useMemo(() => tenantSlug?.trim() || "default", [tenantSlug]);

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);
  const [authRetryAt, setAuthRetryAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const resendSeconds = secondsUntil(resendAvailableAt, now);
  const authRetrySeconds = secondsUntil(authRetryAt, now);

  useEffect(() => {
    if (resendSeconds <= 0 && authRetrySeconds <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [authRetrySeconds, resendSeconds]);

  const applyAuthError = (err: unknown, fallback: string) => {
    if (isApplicantAuthError(err)) {
      const retryAfter = err.payload.retry_after_seconds ?? 0;
      if (retryAfter > 0) {
        const retryAt = Date.now() + retryAfter * 1000;
        if (
          err.payload.error_code === "OTP_RATE_LIMIT" ||
          err.payload.error_code === "OTP_LOCKED" ||
          err.payload.error_code === "OTP_ATTEMPTS_EXCEEDED"
        ) {
          setAuthRetryAt(retryAt);
        }
        if (err.payload.error_code === "OTP_RATE_LIMIT") {
          setResendAvailableAt(retryAt);
        }
      }
      setError(authMessage(err.payload));
      return;
    }

    setError(err instanceof Error ? err.message : fallback);
  };

  const requestOtp = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await requestApplicantOtp(slug, phone);
      setOtpRequested(true);
      const nextResendAt = Date.now() + OTP_RESEND_COOLDOWN_SECONDS * 1000;
      setResendAvailableAt(nextResendAt);
      setAuthRetryAt(null);
      setMessage(
        `OTP sent. Enter the code to continue. You can request another code in ${formatCountdown(
          OTP_RESEND_COOLDOWN_SECONDS
        )}.`
      );
    } catch (err) {
      applyAuthError(err, "Could not request OTP.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = await verifyApplicantOtp(slug, phone, otp);
      const decoded = jwtDecode<ApplicantToken>(data.access_token);

      if (data.role !== "applicant" || decoded.role !== "applicant") {
        throw new Error("Invalid applicant session.");
      }
      if (!decoded.tenant_id) {
        throw new Error("Applicant session is missing tenant context.");
      }

      sessionStorage.setItem("access_token", data.access_token);
      sessionStorage.setItem("refresh_token", data.refresh_token);
      sessionStorage.setItem("tenant_id", String(decoded.tenant_id));
      sessionStorage.setItem("tenant_slug", slug);

      navigate(`/${slug}/applicant/dashboard`, { replace: true });
    } catch (err) {
      applyAuthError(err, "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10">
      <section className="mx-auto max-w-md rounded border bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-950">Applicant Access</h1>
        <p className="mt-1 text-sm text-slate-600">
          Sign in with the phone number used for your application.
        </p>

        <form onSubmit={requestOtp} className="mt-6 space-y-4">
          <label className="block text-sm font-medium text-slate-800">
            Phone number
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              className="mt-1 w-full rounded border px-3 py-2"
              inputMode="tel"
              required
              minLength={10}
              maxLength={15}
            />
          </label>
          <button
            type="submit"
            disabled={loading || !phone || resendSeconds > 0 || authRetrySeconds > 0}
            className="w-full rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {resendSeconds > 0
              ? `Send again in ${formatCountdown(resendSeconds)}`
              : otpRequested
                ? "Send OTP again"
                : "Send OTP"}
          </button>
        </form>

        {otpRequested ? (
          <form onSubmit={verifyOtp} className="mt-6 space-y-4">
            <label className="block text-sm font-medium text-slate-800">
              OTP
              <input
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                className="mt-1 w-full rounded border px-3 py-2"
                inputMode="numeric"
                required
                minLength={6}
                maxLength={6}
              />
            </label>
            <button
              type="submit"
              disabled={loading || otp.length !== 6 || authRetrySeconds > 0}
              className="w-full rounded bg-indigo-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {authRetrySeconds > 0
                ? `Retry in ${formatCountdown(authRetrySeconds)}`
                : "Verify and continue"}
            </button>
          </form>
        ) : null}

        {message ? (
          <p className="mt-4 text-sm text-emerald-700" role="status">
            {message}
          </p>
        ) : null}
        {authRetrySeconds > 0 ? (
          <p className="mt-3 text-sm text-amber-700" role="status">
            Authentication retry is available in {formatCountdown(authRetrySeconds)}.
          </p>
        ) : null}
        {error ? (
          <p className="mt-4 text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : null}
      </section>
    </main>
  );
};

export default ApplicantLogin;
